/**
 * Tenant credit-wallet API wrappers (credit-wallet Phase 5a).
 *
 * Backs `GET /api/companies/:companyId/credits/*`. Every value that crosses this
 * boundary is CREDITS ONLY — the backend already projected away every internal
 * money figure before the wire (the two-view boundary), so nothing here touches
 * a currency amount. Types
 * flow from the Elysia `App` via Eden Treaty and match the `@lexicanon/shared`
 * DTOs 1:1.
 *
 * Mirrors the `getUsage` style in `meetings.ts`: each function takes the
 * company-scoped client (`CompanyApi`) and `unwrap`s the Eden result, then peels
 * the `success(...)` / `paginated(...)` envelope so callers get plain data.
 */

import type { ICreditStatement, ICreditTransactionView, ICreditWalletView } from "@lexicanon/shared"
import type { CompanyApi } from "../../hooks/useCompanyApi.ts"
import { unwrap } from "./errors.ts"

/**
 * Tenant wallet as `GET /credits` returns it. `enforcementEnabled` is the
 * admission-gate flag (off during the pilot). Still credits and dates only.
 */
export interface ITenantCreditWallet extends ICreditWalletView {
  enforcementEnabled: boolean
}

/** One per-meeting statement row, with the latest usage date in the window. */
export interface ITenantCreditMeetingRow {
  meetingId: string
  subject: string | null
  credits: number
  callCount: number
  occurredAt: string | null
}

/** Statement wire shape. Meeting rows always carry a date (or null). */
export interface ITenantCreditStatement extends Omit<ICreditStatement, "byMeeting"> {
  byMeeting: ITenantCreditMeetingRow[]
}

/** One page of the credit ledger — rows plus pagination metadata (credits only). */
export interface ICreditTransactionsPage {
  rows: ICreditTransactionView[]
  total: number
  page: number
  pageSize: number
}

/** Tenant wallet balance — spendable credits, grant/pack split, projected run-out. */
export async function fetchCreditWallet(company: CompanyApi): Promise<ITenantCreditWallet> {
  const body = unwrap(await company.credits.get())
  return body.data
}

/** Paginated credit ledger — grants, top-ups, expiries, adjustments, refunds. */
export async function fetchCreditTransactions(
  company: CompanyApi,
  limit = 50,
  offset = 0,
): Promise<ICreditTransactionsPage> {
  const body = unwrap(await company.credits.transactions.get({ query: { limit, offset } }))
  return { rows: body.data, total: body.meta.total, page: body.meta.page, pageSize: body.meta.pageSize }
}

/** Credit consumption statement over a window — per category + per meeting. */
export async function fetchCreditStatement(
  company: CompanyApi,
  from?: string,
  to?: string,
): Promise<ITenantCreditStatement> {
  const body = unwrap(await company.credits.statement.get({ query: { from, to } }))
  return body.data
}
